import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  SelectChangeEvent,
  IconButton,
  FormHelperText,
  Grid,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon, Clear as ClearIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { AwardCheckResponse, AwardCheckResult, Prerequisites, PrerequisiteRule } from '../types';

export const AWARD_CHECK_ASYNC_ENDPOINT = '/api/award_check_async';

export const awardCheckJobStatusPath = (jobId: string): string => `/api/jobs/${jobId}/status`;

export const awardCheckJobResultPath = (jobId: string): string => `/api/jobs/${jobId}/result`;

export const getAwardCheckErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object') {
    const maybeAxiosError = err as { response?: { data?: { error?: string } }; message?: string };
    return maybeAxiosError.response?.data?.error || maybeAxiosError.message || fallback;
  }
  return fallback;
};

interface AwardCheckAsyncJob {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  progress_message?: string;
  error_message?: string;
}

// Utility function to normalize award names by removing recert/reappointment suffixes
const normalizeAwardName = (name: string): string => {
  return name.replace(/\s+(Reappointment|Recert)$/i, '').trim();
};

// Utility function to consolidate awards by grouping related ones
const consolidateAwards = (awards: string[]): { displayName: string; originalNames: string[] }[] => {
  const groups = new Map<string, string[]>();
  
  awards.forEach(award => {
    const normalized = normalizeAwardName(award);
    if (!groups.has(normalized)) {
      groups.set(normalized, []);
    }
    groups.get(normalized)!.push(award);
  });
  
  return Array.from(groups.entries()).map(([displayName, originalNames]) => ({
    displayName,
    originalNames
  }));
};

interface PrerequisiteRecord {
  Id: string;
  Name: string;
  Course_Name__c: string;
  Prerequisite_Award__c: string;
  Can_Be_Expired__c: boolean;
  Validity_Period__c: number | null;
  Minimum_Age__c: number | null;
}

type CourseName = string;

interface AwardCheckFormDataInput {
  lssIds: string;
  selectedAwards: string[];
  certOption: string;
  prereqMode: boolean;
  selectedCourse: CourseName | '';
  scraperType: 'selenium' | 'playwright';
}

export const buildAwardCheckFormData = ({
  lssIds,
  selectedAwards,
  certOption,
  prereqMode,
  selectedCourse,
  scraperType,
}: AwardCheckFormDataInput): FormData => {
  const formData = new FormData();
  formData.append('lss_ids', lssIds);
  (selectedAwards || []).forEach(award => formData.append('awards', award));
  formData.append('cert_option', certOption);
  if (prereqMode && selectedCourse) {
    formData.append('course', selectedCourse);
    formData.append('prereq_mode', 'on');
  }
  formData.append('scraper_type', scraperType);
  formData.append('headless', 'true');
  return formData;
};

const AwardCheck = () => {
  const [lssIds, setLssIds] = useState('');
  const [availableAwards, setAvailableAwards] = useState<string[]>([]);
  const [selectedAwards, setSelectedAwards] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<AwardCheckResult[]>([]);
  const [allAwardData, setAllAwardData] = useState<AwardCheckResult[]>([]);
  const [searchTime, setSearchTime] = useState(0);
  const [prereqMode, setPrereqMode] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<CourseName | ''>('');
  const [courseOptions, setCourseOptions] = useState<CourseName[]>([]);
  const [courseAwardMap, setCourseAwardMap] = useState<Prerequisites>({});
  const [certOption, setCertOption] = useState('Complete');
  const [asyncJob, setAsyncJob] = useState<AwardCheckAsyncJob | null>(null);
  const [scraperType, setScraperType] = useState<'selenium' | 'playwright'>('playwright');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        
        const [awardsResponse, prereqResponse] = await Promise.all([
          axios.get<AwardCheckResponse>('/api/award_check_ajax'),
          axios.get<PrerequisiteRecord[]>('/api/prerequisites')
        ]);

        if (!awardsResponse.data?.available_awards) {
          throw new Error('Invalid awards data format');
        }
        
        if (!Array.isArray(prereqResponse.data)) {
          throw new Error('Invalid prerequisites data format');
        }

        const newCourseAwardMap: Prerequisites = {};
        for (const record of prereqResponse.data) {
            const courseName = record.Course_Name__c;
            if (!newCourseAwardMap[courseName]) {
                newCourseAwardMap[courseName] = [];
            }
            newCourseAwardMap[courseName].push({
                award: record.Prerequisite_Award__c,
                allowExpired: record.Can_Be_Expired__c,
                validityPeriod: record.Validity_Period__c,
                minimumAge: record.Minimum_Age__c,
            });
        }

        setAvailableAwards(awardsResponse.data.available_awards);
        setCourseAwardMap(newCourseAwardMap);
        setCourseOptions(Object.keys(newCourseAwardMap));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
        setError(errorMessage);
        setAvailableAwards([]);
        setCourseAwardMap({});
        setCourseOptions([]);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (prereqMode && selectedCourse) {
      const awards = courseAwardMap[selectedCourse]?.map(rule => rule.award) || [];
      setSelectedAwards(awards);
    }
  }, [prereqMode, selectedCourse, courseAwardMap]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lssIds.trim()) {
      setError('Please enter at least one LSS ID');
      return;
    }

    setLoading(true);
    setError(null);

    const ids = lssIds.split('\n').filter(id => id.trim());
    if (ids.length === 0) {
      setError('Please enter at least one valid LSS ID');
      setLoading(false);
      return;
    }

    try {
      const formData = buildAwardCheckFormData({
        lssIds,
        selectedAwards,
        certOption,
        prereqMode,
        selectedCourse,
        scraperType,
      });

      setAsyncJob(null);
      const response = await axios.post<{ job_id: string }>(AWARD_CHECK_ASYNC_ENDPOINT, formData);
      const jobId = response.data?.job_id;

      if (!jobId) {
        throw new Error('Invalid async job response from server');
      }

      let isComplete = false;
      while (!isComplete) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const statusResponse = await axios.get<AwardCheckAsyncJob>(awardCheckJobStatusPath(jobId));
        const job = statusResponse.data;
        setAsyncJob(job);

        if (job.status === 'failed') {
          throw new Error(job.error_message || 'Award check job failed');
        }

        if (job.status === 'completed') {
          const resultResponse = await axios.get<{ result: AwardCheckResponse }>(awardCheckJobResultPath(jobId));
          const awardCheckResult = resultResponse.data?.result;

          if (!awardCheckResult?.results) {
            throw new Error('Invalid async result format from server');
          }

          setAllAwardData(awardCheckResult.results);
          setResults(awardCheckResult.results);
          setSearchTime(awardCheckResult.searchTime || 0);
          isComplete = true;
        }
      }
    } catch (err) {
      const errorMessage = getAwardCheckErrorMessage(err, 'Failed to check awards');
      setError(errorMessage);
      setResults([]);
      setAllAwardData([]);
      setAsyncJob(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAwardChange = (event: SelectChangeEvent<string[]>) => {
    const newSelectedAwards = event.target.value as string[];
    setSelectedAwards(newSelectedAwards);
    
    if (allAwardData?.length > 0) {
      const filteredResults = allAwardData.map(result => ({
        ...result,
        AwardsChecked: newSelectedAwards,
        Matches: Object.fromEntries(
          (newSelectedAwards || []).map(award => [award, result.Matches?.[award] || false])
        ),
        ExpiryDetails: Object.fromEntries(
          (newSelectedAwards || []).map(award => [
            award, 
            result.ExpiryDetails?.[award] || { expiry: '', daysLeft: undefined }
          ])
        ),
        AllMatched: (newSelectedAwards || []).every(award => result.Matches?.[award])
      }));
      setResults(filteredResults);
    }
  };

  const handleCourseChange = (event: SelectChangeEvent<CourseName | ''>) => {
    const course = event.target.value as CourseName;
    setSelectedCourse(course);
    if (course && courseAwardMap[course]) {
      const awards = courseAwardMap[course]?.map(rule => rule.award) || [];
      setSelectedAwards(awards);
    } else {
      setSelectedAwards([]);
    }
  };

  const handleDownloadXlsx = async () => {
    if (!results?.length) {
      setError('No results to download');
      return;
    }

    try {
      const response = await axios.post('/api/award_check_download', 
        { results },
        { responseType: 'blob' }
      );
      
      if (!response.data) {
        throw new Error('No data received from server');
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'award_check_results.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download results';
      setError(errorMessage);
    }
  };

  const handleClearData = () => {
    setLssIds('');
    setResults([]);
    setAllAwardData([]);
    setError(null);
    setSearchTime(0);
    setAsyncJob(null);
  };

  if (initialLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading initial data...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom align="center">
          Award Checker
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
          <strong>Paste your LSS IDs</strong> (one per line) below,
          then either toggle <strong>Prerequisite Mode</strong> to pick a course
          or select awards manually. Finally, choose which certifications to include
          and click <em>"Check Awards."</em>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Prerequisite Mode Toggle */}
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={prereqMode}
                    onChange={() => setPrereqMode((prev) => !prev)}
                    color="primary"
                  />
                }
                label={<Typography variant="subtitle1"><strong>Prerequisite Mode</strong></Typography>}
              />
            </Grid>

            {/* LSS IDs and Awards/Course Selection */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={8}
                label="LSS IDs (one per line)"
                value={lssIds}
                onChange={(e) => setLssIds(e.target.value.toUpperCase())}
                required
                helperText="Hold Command (Mac) or Ctrl (Windows) to select multiple"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {prereqMode ? (
                <FormControl fullWidth>
                  <InputLabel>Select a Course</InputLabel>
                  <Select
                    value={selectedCourse}
                    onChange={handleCourseChange}
                    label="Select a Course"
                  >
                    {courseOptions.map((course) => (
                      <MenuItem key={course} value={course}>
                        {course}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Select Awards to Check</InputLabel>
                  <Select
                    multiple
                    value={selectedAwards}
                    onChange={handleAwardChange}
                    label="Select Awards to Check"
                    renderValue={(selected) => (selected as string[]).join(', ')}
                  >
                    {availableAwards.map((award) => (
                      <MenuItem key={award} value={award}>
                        <Checkbox checked={selectedAwards.includes(award)} />
                        {award}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Hold Command/Ctrl to select multiple awards
                  </FormHelperText>
                </FormControl>
              )}
            </Grid>

            {/* Certification Options */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Which Certifications to Include?</InputLabel>
                <Select
                  value={certOption}
                  onChange={(e) => setCertOption(e.target.value)}
                  label="Which Certifications to Include?"
                >
                  <MenuItem value="Complete">All Certifications</MenuItem>
                  <MenuItem value="Current">Current Certifications Only</MenuItem>
                  <MenuItem value="Concise">First & Most Recent of Each Certification</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Scraper Engine Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Scraper Engine</InputLabel>
                <Select
                  value={scraperType}
                  onChange={(e) => setScraperType(e.target.value as 'selenium' | 'playwright')}
                  label="Scraper Engine"
                >
                  <MenuItem value="selenium">Selenium</MenuItem>
                  <MenuItem value="playwright">Playwright</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {scraperType === 'playwright' 
                  ? 'Playwright: Faster and more reliable' 
                  : 'Selenium: Traditional browser automation'}
              </Typography>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !lssIds.trim() || selectedAwards.length === 0}
                sx={{ mr: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Check Awards'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearData}
                startIcon={<ClearIcon />}
                sx={{ mr: 2 }}
              >
                Clear Data
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownloadXlsx}
                startIcon={<DownloadIcon />}
                disabled={!results.length}
              >
                Download XLSX
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Progress Message */}
        {loading && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="primary" sx={{ fontWeight: 'bold' }}>
              {asyncJob?.progress_message || 'Queuing LSS ID checks...'}
            </Typography>
            {asyncJob ? (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={asyncJob.progress || 0} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {asyncJob.status} - {asyncJob.progress || 0}%
                </Typography>
              </Box>
            ) : (
              <CircularProgress sx={{ mt: 1 }} />
            )}
          </Box>
        )}

        {results.length > 0 && (
          <Box sx={{ mt: 4 }}>
            {/* Summary Bar */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 2, 
              p: 1, 
              border: '1px solid #ccc', 
              borderRadius: 1, 
              background: '#f9f9f9', 
              fontSize: '0.95rem', 
              color: '#444', 
              justifyContent: 'center' 
            }}>
              <span><strong>IDs:</strong> {results.length}</span>
              <span><strong>Awards searched:</strong> {consolidateAwards(selectedAwards || []).map(group => group.displayName).join(', ')}</span>
              <span><strong>Time:</strong> {searchTime}s</span>
            </Box>

            {/* Results Table */}
            <TableContainer component={Paper} className="award-check-results-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>LSS ID</TableCell>
                    {consolidateAwards(selectedAwards || []).map((awardGroup) => (
                      <TableCell key={awardGroup.displayName}>{awardGroup.displayName}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result, idx) => {
                    const invalid = result.Name === 'INVALID LSS ID' || result.Name === 'INVALID FORMAT';
                    let rowColor = invalid ? '#fff3b3' : result.AllMatched ? '#e3fae3' : '#f8d2d2';
                    const consolidatedAwards = consolidateAwards(selectedAwards || []);
                    
                    return (
                      <TableRow key={idx} sx={{ backgroundColor: rowColor }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          {result.Name}
                          {result.Error && <Typography variant="body2" color="error">{result.Error}</Typography>}
                        </TableCell>
                        <TableCell>{result["LSS ID"]}</TableCell>
                        {consolidatedAwards.map((awardGroup) => {
                          // Check if any of the original award names in this group have a match
                          let hasMatch = false;
                          let bestExpiry = null;
                          let bestDaysLeft = null;
                          let color = 'inherit';
                          let cellContent: string | JSX.Element = '';
                          
                          // Check all original names in this group for matches and expiry info
                          for (const originalName of awardGroup.originalNames) {
                            const normalizedName = normalizeAwardName(originalName);
                            const match = result.Matches[normalizedName];
                            const expiry = result.ExpiryDetails[normalizedName]?.expiry;
                            const daysLeft = result.ExpiryDetails[normalizedName]?.daysLeft;
                            
                            if (match) {
                              hasMatch = true;
                              // If we have expiry info, use the one with the latest expiry date
                              if (expiry && daysLeft !== undefined) {
                                if (!bestExpiry || (daysLeft > (bestDaysLeft || -Infinity))) {
                                  bestExpiry = expiry;
                                  bestDaysLeft = daysLeft;
                                }
                              }
                            }
                          }
                          
                          // Determine cell content and color
                          if (bestExpiry !== null && bestDaysLeft !== null) {
                            color = bestDaysLeft >= 0 ? 'green' : 'red';
                            cellContent = (
                              <Box>
                                <Typography variant="body2">{bestExpiry}</Typography>
                                <Typography 
                                  variant="body2" 
                                  color={bestDaysLeft >= 0 ? 'success.main' : 'error.main'}
                                >
                                  Days until expiry: {bestDaysLeft}
                                </Typography>
                              </Box>
                            );
                          } else if (hasMatch) {
                            color = 'green';
                            cellContent = '✓';
                          } else {
                            color = 'red';
                            cellContent = '✗';
                          }
                          
                          return (
                            <TableCell key={awardGroup.displayName} sx={{ color }}>
                              {cellContent}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AwardCheck;
